import type { Poll, PollGroup, PollOption } from '@tunasong/schemas'

/**
 * Rebase a co-editor's poll patch onto the version saved since, without moving any vote (GH #1912).
 *
 * Poll responses and `finalOption` point at options by INDEX, and options are never spliced (a removal is the
 * `removed` tombstone). Writing a whole `pollOptions` array last-write-wins can therefore put a different option
 * at an index somebody voted for: two co-editors who each add an option leave the later one at the index of the
 * earlier. This merge keeps every index the server already has where it is (Joni's rule):
 *   - an option only one side changed takes that side's version;
 *   - an option both sides changed keeps theirs, and mine is added as a new option;
 *   - a tombstone set on either side is never cleared;
 *   - their additions come first, then mine — and my `finalOption` follows my options to their new indexes.
 * Groups are referenced by id, not position, so they merge per id (theirs wins a same-group edit).
 *
 * `base` is the version my patch was made from, `theirs` the server's version now. Returns the patch to send on
 * `theirs`, or `undefined` when the arrays are not in a state this can merge (a spliced list), so the caller can
 * fall back.
 */
export const mergePollPatch = ({
  base,
  theirs,
  patch,
}: {
  base: Poll
  theirs: Poll
  patch: Partial<Poll>
}): Partial<Poll> | undefined => {
  const merged: Partial<Poll> = { ...patch }

  if (patch.pollOptions) {
    const options = mergeOptions(base.pollOptions ?? [], theirs.pollOptions ?? [], patch.pollOptions)
    if (!options) {
      return undefined
    }
    merged.pollOptions = options.pollOptions
    if (patch.finalOption !== undefined) {
      merged.finalOption = remapFinalOption(patch.finalOption, options.mineAt)
    }
  }
  if (patch.pollGroups) {
    merged.pollGroups = mergeGroups(base.pollGroups ?? [], theirs.pollGroups ?? [], patch.pollGroups)
  }
  return merged
}

/** Both values come from the same serializer, so equal content serializes equally */
const same = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b)

const withoutTombstone = <T extends { removed?: boolean }>(item: T) => {
  const { removed: _removed, ...rest } = item
  return rest
}

/** A tombstone set anywhere stays set */
const tombstoned = <T extends { removed?: boolean }>(item: T, ...others: (T | undefined)[]): T =>
  [item, ...others].some(other => other?.removed) ? { ...item, removed: true } : item

const mergeOptions = (
  base: PollOption[],
  theirs: PollOption[],
  mine: PollOption[]
): { pollOptions: PollOption[]; mineAt: Map<number, number> } | undefined => {
  /** Never spliced: either list shorter than the base is a state no index-preserving merge can repair */
  if (theirs.length < base.length || mine.length < base.length) {
    return undefined
  }
  const pollOptions: PollOption[] = []
  /** Where each of my indexes ends up */
  const mineAt = new Map<number, number>()
  /** My versions of options both sides changed, appended after their additions with my own */
  const appendedMine: { from: number; option: PollOption }[] = []

  base.forEach((baseOption, index) => {
    const their = theirs[index]
    const my = mine[index]
    const theyChanged = !same(their, baseOption)
    const iChanged = !same(my, baseOption)
    if (!iChanged || !theyChanged) {
      pollOptions.push(tombstoned(iChanged ? my : their, baseOption, their, my))
      mineAt.set(index, index)
      return
    }
    /** Both changed. A difference only in the tombstone is no conflict: the removal holds */
    pollOptions.push(tombstoned(their, baseOption, my))
    mineAt.set(index, index)
    if (!same(withoutTombstone(my), withoutTombstone(baseOption)) && !my.removed) {
      appendedMine.push({ from: index, option: withoutTombstone(my) })
    }
  })

  pollOptions.push(...theirs.slice(base.length))
  for (const { from, option } of appendedMine) {
    mineAt.set(from, pollOptions.length)
    pollOptions.push(option)
  }
  mine.slice(base.length).forEach((option, offset) => {
    mineAt.set(base.length + offset, pollOptions.length)
    pollOptions.push(option)
  })
  return { pollOptions, mineAt }
}

/** `finalOption` is an option index (one-of-many) or a set of indexes (many-of-many) */
const remapFinalOption = (finalOption: unknown, mineAt: Map<number, number>): unknown => {
  if (typeof finalOption === 'number') {
    return mineAt.get(finalOption) ?? finalOption
  }
  if (finalOption && typeof finalOption === 'object') {
    return Object.fromEntries(
      Object.entries(finalOption as Record<string, unknown>).map(([index, value]) => [
        String(mineAt.get(Number(index)) ?? index),
        value,
      ])
    )
  }
  return finalOption
}

const mergeGroups = (base: PollGroup[], theirs: PollGroup[], mine: PollGroup[]): PollGroup[] => {
  const baseById = new Map(base.map(group => [group.id, group]))
  const mineById = new Map(mine.map(group => [group.id, group]))
  const merged = theirs.map(their => {
    const baseGroup = baseById.get(their.id)
    const my = mineById.get(their.id)
    /** Theirs wins a group both sides changed; mine wins where only I changed it */
    const chosen = my && baseGroup && same(their, baseGroup) ? my : their
    return tombstoned(chosen, baseGroup, their, my)
  })
  const theirIds = new Set(theirs.map(group => group.id))
  /** Groups only I have: my additions, in my order */
  return [...merged, ...mine.filter(group => !theirIds.has(group.id))]
}
